/**
 * Панель «Ракурсы» на первом шаге («1 · Ракурсы») видео-проекта «из кадра» (видео-ролик из готового кадра,
 * §7/§8/§10/§11 спеки `2026-10-13-frame-to-video-design.md`): строка исходной генерации со списком
 * «какой это ракурс», список ракурсов со статусами, оценка пакета, «Сгенерировать ракурсы» (ПЛАТНО),
 * у каждого кадра — «выбрать / сменить кадр» и «кадрировать 9:16». Промпта ДВИЖЕНИЯ тут нет: он
 * уехал на шаг «2 · Видео», панель «Движение кадров» (№335) — рядом с заданием ракурса два промпта,
 * на фото и на видео, путались. Здесь только промпт фото — задание ракурса в строке.
 *
 * Ракурс исходной генерации сотрудник называет САМ (задача Оли №305, ответ №307 — «пока не делай
 * автоподстановку», №312 — «выбрать из списка»): названный ракурс становится готовым без генерации,
 * и пакет берёт только то, чего нет.
 *
 * «Перегенерировать» — в строке ГОТОВОГО ракурса (задача Оли №313): тот же пакет на один код с
 * флагом `replace`, новый кадр заменит прежний стилл, когда будет готов. Рядом с массовым запуском
 * недостающих ракурсов, а не вместо него.
 *
 * «Сгенерировать» — в строке ПУСТОГО (задача Оли №320: «кнопка должна быть в блоке каждого ракурса,
 * а не одна внизу»): тот же пакет на один код, без `replace`; по готовности строки её место занимает
 * «Перегенерировать». Нижняя кнопка осталась как ускорение и называет себя «все недостающие»: пакет
 * из трёх ракурсов — один клик и один расчёт потолка.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clapperboard, Crop, ImageUp, Minus, Plus, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { CropDialog } from "@/components/video/CropDialog";
import { ShotStillPicker } from "@/components/video/ShotStillPicker";
import { InlineText } from "@/components/project/InlineText";
import { Btn, ErrorLine, Hint, Panel, selectClass } from "@/components/project/ui";
import { fittingKeys, loadFittingOptions } from "@/lib/fitting";
import { cropTooSmall, offRatio } from "@/lib/frameRatio";
import { hasStill } from "@/lib/videoCompare";
import { mediaUrl } from "@/lib/media";
import { browserStore, defaultModelCode } from "@/lib/promptMode";
import { errorText, postJson, qk, usd, type Shot } from "@/lib/project";
import { resultPath } from "@/lib/results";
import { ANGLE_CODES, ANGLE_MODEL_KEY, anglesFor, canDropView, type AngleCode } from "@/lib/videoAngles";
import {
  videoKeys,
  useRunViews,
  useSetSourceView,
  useSetViews,
  useViewsEstimate,
  type TimelineShot,
} from "@/lib/video";

type AngleRow = { code: AngleCode; label: string; shot: TimelineShot | undefined };

function latestGen(shot: Shot | undefined) {
  if (!shot || shot.gens.length === 0) return null;
  return [...shot.gens].sort((a, b) => b.generation_id - a.generation_id)[0] ?? null;
}

/**
 * Превью кадра в панели (задача Оли №313: «превью должна быть крупнее, чтобы видеть результат
 * генерации»). Было 48×72 — по такой картинке нельзя судить о генерации. 108×192 — это 9:16, та же
 * пропорция, в которой ракурсы теперь и генерируются (№315). `object-contain`, а не `cover`:
 * обрезка съедала бы ровно то, что сотрудник пришёл оценить, а старые кадры 2:3 резала бы косо.
 */
const PREVIEW_BOX =
  "h-[192px] w-[108px] flex-none overflow-hidden rounded-[3px] border border-border";


export function AnglesPanel({ pid, shots, timeline }: { pid: number; shots: readonly Shot[]; timeline: readonly TimelineShot[] }) {
  const byId = new Map(shots.map((s) => [s.id, s]));
  const rows: AngleRow[] = anglesFor(timeline).map((a) => ({ ...a, shot: timeline.find((t) => t.framing === a.code) }));
  const missing = rows.filter((r) => r.shot && !hasStill(r.shot.still)).map((r) => r.code);
  // Уже поставленные в очередь ракурсы из «недостающих» вычитаем (найдено ревью 16.10): иначе нижняя
  // кнопка обещает платить за оплаченное, а потолок проекта считает их заново и отказывает зря
  // (`_frame_view_new_cost` видит другой ключ пакета).
  const queued = new Set(
    rows
      .filter((r) => {
        const last = r.shot ? latestGen(byId.get(r.shot.shot_id)) : null;
        return last?.status === "queued" || last?.status === "running";
      })
      .map((r) => r.code),
  );
  const toRun = missing.filter((c) => !queued.has(c));
  // Исходная генерация проекта: её кадр сервер помечает `source` — по нему же он держит кадр первым
  // на дорожке и запрещает убрать его составом ракурсов.
  const source = timeline.find((t) => t.source);
  const sourceCode = ANGLE_CODES.find((c) => c === source?.framing) ?? "";
  const setSourceView = useSetSourceView(pid);

  const options = useQuery({ queryKey: fittingKeys.options, queryFn: loadFittingOptions, staleTime: 60_000 });
  const models = (options.data?.models ?? []).filter((m) => !m.disabled);
  const store = browserStore();
  const [model, setModel] = useState("");
  useEffect(() => {
    try {
      setModel(store?.getItem(ANGLE_MODEL_KEY) ?? "");
    } catch {
      setModel("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- читаем сохранённую модель один раз
  }, []);
  const current = model && models.some((m) => m.code === model) ? model : defaultModelCode(models);

  const estimate = useViewsEstimate(pid, { model: current, angles: toRun }, toRun.length > 0);
  // Отдельная оценка для ГОТОВЫХ ракурсов: пакетная считает только недостающие, а цену
  // «Перегенерировать» показать надо — кнопка платная (№313). Запрос бесплатный, ничего не пишет.
  const readyCodes = rows.filter((r) => r.shot && hasStill(r.shot.still) && !r.shot.source).map((r) => r.code);
  const redoEstimate = useViewsEstimate(pid, { model: current, angles: readyCodes }, readyCodes.length > 0);
  const redoCost = (code: AngleCode) => redoEstimate.data?.items.find((i) => i.angle === code)?.cost ?? null;
  const run = useRunViews(pid);
  /** Выбранную модель помнит браузер — и после №320 запускают чаще всего из СТРОКИ, поэтому
   *  запоминаем во всех трёх путях, а не только в массовом (найдено ревью 16.10). */
  const remember = () => {
    try {
      store?.setItem(ANGLE_MODEL_KEY, current);
    } catch {
      // без памяти браузера — не критично
    }
  };
  const generate = () => {
    if (toRun.length === 0 || run.busy) return;
    remember();
    run.run({ model: current, angles: toRun });
  };
  /** Перегенерация одного ракурса (№313): тот же пакет на один код, новый кадр заменит стилл. */
  const regenerate = (code: AngleCode) => {
    if (run.busy) return;
    remember();
    run.run({ model: current, angles: [code], replace: true });
  };
  /** Генерация одного пустого ракурса (задача Оли №320): тот же пакет на один код, без `replace`. */
  const generateOne = (code: AngleCode) => {
    if (run.busy) return;
    remember();
    run.run({ model: current, angles: [code] });
  };
  /** Цена строки пустого ракурса — из пакетной оценки: она уже считает ровно недостающие. */
  const newCost = (code: AngleCode) => estimate.data?.items.find((i) => i.angle === code)?.cost ?? null;

  const [pickerFor, setPickerFor] = useState<number | null>(null);
  const pickerRow = rows.find((r) => r.shot?.shot_id === pickerFor);
  // Кадрирование под 9:16 (задача Оли №323): пропорцию узнаём по загруженному превью — размеров
  // стилла в таймлайне нет, а тащить их с сервера ради подписи незачем.
  const [sizes, setSizes] = useState<Record<number, { w: number; h: number }>>({});
  const [cropFor, setCropFor] = useState<number | null>(null);
  // По ТАЙМЛАЙНУ, а не по `rows`: у исходного кадра свежего проекта `shot_framing` пуст (ракурс ему
  // ещё не назвали), и в каталожных строках его нет — окно не открывалось бы именно в том случае,
  // ради которого задача и заводилась (найдено ревью 16.10).
  const cropShot = cropFor === null ? undefined : timeline.find((t) => t.shot_id === cropFor);

  const noteSize = (shotId: number, img: HTMLImageElement) =>
    setSizes((s) =>
      s[shotId]?.w === img.naturalWidth && s[shotId]?.h === img.naturalHeight
        ? s
        : { ...s, [shotId]: { w: img.naturalWidth, h: img.naturalHeight } },
    );

  // Состав ракурсов (задача Оли №263): «убрать» и «добавить» — обе кнопки шлют ЦЕЛЕВОЙ состав,
  // а не дельту: сервер приводит кадры проекта к списку, и две открытые вкладки не спорят о дельтах.
  const client = useQueryClient();
  const setViews = useSetViews(pid);
  // Состав считаем от ОТВЕТА прошлой мутации, пока не приехал свежий таймлайн: `isPending` гаснет
  // раньше перезапроса, и второй клик в это окно отправил бы состав без только что добавленного
  // ракурса — сервер послушно удалил бы его вместе с вписанным заданием (ревью 14.10).
  const answered = setViews.data ? [...setViews.data.kept, ...setViews.data.added] : null;
  const fromTimeline = rows.filter((r) => r.shot).map((r) => r.code);
  const included = (answered ?? fromTimeline).filter((c): c is AngleCode => ANGLE_CODES.includes(c as AngleCode));
  const changeSet = (code: AngleCode, on: boolean) => {
    if (setViews.isPending) return;
    const next = on ? [...new Set([...included, code])] : included.filter((c) => c !== code);
    setViews.mutate(next);
  };

  // Задание ракурса (задача Оли №264) — тем же полем кадра и той же ручкой, что правка кадра в
  // `ShotCard`: своего редактора не заводим, иначе история правок кадра разъехалась бы с панелью.
  const saveTask = async (shotId: number, value: string) => {
    const result = await postJson<{ value: string; prompt_resolved: string | null }>(
      `/api/projects/${pid}/shots/${shotId}/field`,
      { field: "shot_prompt", value },
    );
    void client.invalidateQueries({ queryKey: qk.project(pid) });
    void client.invalidateQueries({ queryKey: qk.shot(pid, shotId) });  // карточка кадра с историей правок
    void client.invalidateQueries({ queryKey: videoKeys.poll(pid) });
    return result.value;
  };

  return (
    <Panel
      title="Ракурсы"
      aside={
        <select
          value={current}
          onChange={(e) => setModel(e.target.value)}
          aria-label="модель ракурсов"
          className={selectClass}
        >
          {(models.length ? models : [{ code: current, label: current }]).map((m) => (
            <option key={m.code} value={m.code}>
              {m.label}
            </option>
          ))}
        </select>
      }
      annot="ракурсы кадра"
    >
      {source ? (
        <div
          className="mb-2 flex items-start gap-3 rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2"
          data-annot="исходный кадр проекта"
        >
          <div className={PREVIEW_BOX + " bg-bg"}>
            {source.still ? (
              <Link to={resultPath(source.still.variant_id)} title="карточка исходного кадра">
                <img
                  src={mediaUrl(source.still.asset_path)}
                  alt=""
                  className="h-full w-full object-contain"
                  onLoad={(e) => noteSize(source.shot_id, e.currentTarget)}
                />
              </Link>
            ) : (
              <div className="grid h-full w-full place-items-center text-[11px] text-fg-faint">нет</div>
            )}
          </div>
          {/* Колонка на всю оставшуюся ширину, список ракурса — ПОД подписью (задача Оли №324:
              «перекомпоновать элементы внутри блока чтобы не было пустоты»): третьей колонкой он
              сжимал текст в узкий столбик, а под высоким превью оставалось пустое место. */}
          <div className="grid min-w-0 flex-1 gap-1 text-[12.5px]">
            <b className="text-fg">Исходный кадр</b>
            <span className="text-fg-muted">
              ваша генерация — она в ролике уже есть, генерировать её не нужно
            </span>
            {offRatio(sizes[source.shot_id]) ? (
              <span className="text-[11.5px] text-warn">
                кадр {sizes[source.shot_id]!.w}×{sizes[source.shot_id]!.h} — не 9:16, в ролике обрежется по центру
              </span>
            ) : null}
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {sourceCode === "" && source.still && offRatio(sizes[source.shot_id]) && !cropTooSmall(sizes[source.shot_id]) ? (
                <Btn
                  tone="ghost"
                  title="бесплатно: обрезать кадр рамкой 9:16; оригинал останется в «Результатах»"
                  onClick={() => setCropFor(source.shot_id)}
                >
                  <Crop size={13} />
                  кадрировать 9:16
                </Btn>
              ) : null}
            </div>
            <label className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-fg-muted">
              какой это ракурс
              <select
                value={sourceCode}
                disabled={setSourceView.isPending}
                aria-label="ракурс исходного кадра"
                className={selectClass}
                onChange={(e) => setSourceView.mutate(e.target.value || null)}
              >
                <option value="">не указан</option>
                {anglesFor(timeline).map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      ) : null}
      <ErrorLine text={setSourceView.isError ? errorText(setSourceView.error) : ""} className="mb-2" />

      <div className="grid gap-2">
        {rows.map((r) => {
          const shot = r.shot;
          const ready = shot ? hasStill(shot.still) : false;
          // Ракурс занял сам исходник (его назвали списком выше): кадр уже есть, генерировать нечего,
          // и ни «убрать», ни «сменить кадр» ему не предлагаем — ракурс снимают там же, где назвали.
          const isSource = shot?.source === true;
          const latest = shot ? latestGen(byId.get(shot.shot_id)) : null;
          // Статус СВЕЖЕЙ генерации считаем независимо от `ready` (ревью 16.10): у перегенерации
          // старый стилл на месте, то есть `ready` истинно всё время, пока идёт новый кадр. Со
          // старым условием `!ready && …` строка писала «готово» и в очереди, и после падения —
          // сотрудник не видел ни работы, ни того, что оплаченный запуск не вышел.
          const generating = latest?.status === "queued" || latest?.status === "running";
          const failed = latest?.status === "failed";
          // Ракурс сняли галочкой при создании проекта — кадра под него нет вовсе: не «ждёт очереди»,
          // а не участвует в проекте — иначе строка неотличима от того, что можно поставить в очередь.
          const status = !shot
            ? { text: "не включён в проект", cls: "text-fg-faint" }
            : isSource
              ? { text: "готово · это ваш исходный кадр", cls: "text-ok" }
              : generating
                ? { text: ready ? "готово · перегенерация в работе…" : "генерируется…", cls: "text-accent-text" }
                : failed
                  ? {
                      text: `${ready ? "готово · перегенерация не вышла" : "ошибка"}: ${latest?.generation_error || "не удалось"}`,
                      cls: "text-err",
                    }
                  : ready
                    ? { text: "готово", cls: "text-ok" }
                    : { text: "не сгенерирован", cls: "text-fg-muted" };
          return (
            <div
              key={r.code}
              className="flex items-start gap-3 rounded-[var(--radius-ui)] border border-border bg-bg p-2"
            >
              <div className={PREVIEW_BOX + " bg-bg-subtle"}>
                {ready && shot?.still ? (
                  <Link to={resultPath(shot.still.variant_id)}>
                    <img
                      src={mediaUrl(shot.still.asset_path)}
                      alt=""
                      className="h-full w-full object-contain"
                      onLoad={(e) => noteSize(shot.shot_id, e.currentTarget)}
                    />
                  </Link>
                ) : (
                  <div className="grid h-full w-full place-items-center text-[11px] text-fg-faint">нет</div>
                )}
              </div>
              {/* Одна колонка на всю оставшуюся ширину: сверху текст, под ним кнопки с переносом
                  (задача Оли №324 — «перекомпоновать элементы внутри блока чтобы не было пустоты»).
                  Раньше кнопки стояли третьей колонкой и не переносились: задание ракурса сжималось
                  в столбик по три слова, а под высоким превью зияла пустота. */}
              <div className="grid min-w-0 flex-1 gap-2">
                <div className="grid gap-0.5 text-[12.5px]">
                  <b className="text-fg">{r.label}</b>
                  <span className={status.cls}>{status.text}</span>
                  {ready && shot && !isSource && offRatio(sizes[shot.shot_id]) ? (
                    <span className="text-[11px] text-warn">
                      кадр {sizes[shot.shot_id]!.w}×{sizes[shot.shot_id]!.h} — не 9:16, в ролике обрежется по центру
                    </span>
                  ) : null}
                  {shot && !isSource ? (
                    <div className="grid gap-0.5">
                      {/* Значение, а не placeholder: «поменять шаблон» не должно означать «набрать
                          английскую фразу заново» — щелчок открывает текст, который реально уйдёт в модель. */}
                      <InlineText
                        value={shot.view_task ?? shot.view_task_default}
                        placeholder="задание ракурса (по-английски)"
                        multiline
                        onSave={(v) => saveTask(shot.shot_id, v)}
                        className="text-[11.5px]"
                        annot={`задание ракурса ${r.label}`}
                      />
                      <span className="text-[10.5px] text-fg-faint">
                        {shot.view_task === null ? "задание по умолчанию, по-английски" : "своё задание, по-английски"}
                      </span>
                    </div>
                  ) : null}
                  {shot && ready && !isSource ? (
                    // Готовый ракурс пакет «по умолчанию» не берёт, но правка задания больше не
                    // бесполезна: рядом кнопка «Перегенерировать» (№313), которая отправит ровно
                    // этот текст. Прежняя оговорка «перегенерация — на карточке стилла» снята.
                    <span className="text-[11px] text-fg-faint">
                      правка задания уйдёт в «Перегенерировать»
                    </span>
                  ) : null}
                </div>
                {shot ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {/* Кадрирование есть и у строки ИСХОДНОГО кадра (задача Оли №334: «нет возможности
                        кадрирования» — она смотрела в строку «Средний», занятую исходником, а кнопка
                        была только в блоке выше). Остальные кнопки исходнику не нужны: убрать и
                        сменить его нельзя, генерировать нечего. */}
                    {ready && offRatio(sizes[shot.shot_id]) && !cropTooSmall(sizes[shot.shot_id]) ? (
                      <Btn
                        tone="ghost"
                        title="бесплатно: обрезать кадр рамкой 9:16; оригинал останется в «Результатах»"
                        onClick={() => setCropFor(shot.shot_id)}
                      >
                        <Crop size={13} />
                        кадрировать 9:16
                      </Btn>
                    ) : null}
                    {isSource ? null : (
                      <>
                        <Btn
                          tone="ghost"
                          disabled={
                            !canDropView({ still: shot.still, gens: byId.get(shot.shot_id)?.gens ?? [], source: shot.source }) ||
                            setViews.isPending
                          }
                          title={
                            canDropView({ still: shot.still, gens: byId.get(shot.shot_id)?.gens ?? [], source: shot.source })
                              ? "убрать ракурс из проекта (бесплатно, до генерации)"
                              : "ракурс уже сгенерирован — его кадр убирают на дорожке"
                          }
                          onClick={() => changeSet(r.code, false)}
                        >
                          <Minus size={13} />
                          убрать
                        </Btn>
                        {/* Подпись по состоянию стилла (задача Оли №308): пустому кадру «сменить» нечего —
                            он берёт готовый кадр из «Результатов», и это бесплатно (№311, №312). */}
                        <Btn
                          tone="ghost"
                          onClick={() => setPickerFor(shot.shot_id)}
                          title={
                            ready
                              ? "выбрать другую картинку этого кадра из «Результатов» (бесплатно)"
                              : "взять готовый кадр из «Результатов» вместо генерации (бесплатно)"
                          }
                        >
                          <ImageUp size={13} />
                          {ready ? "сменить кадр" : "выбрать кадр"}
                        </Btn>
                      {/* Задача Оли №320: «кнопка сгенерировать ракурс должна быть в блоке каждого
                          ракурса, а не одна внизу» — у пустого это тот же пакет на один код, и по
                          готовности строки она сменяется «перегенерировать». */}
                      {ready ? null : (
                        <Btn
                          tone="primary"
                          disabled={run.busy || generating || estimate.isPending}
                          title={
                            generating
                              ? "ракурс уже в очереди"
                              : "ПЛАТНО: сгенерировать этот ракурс; оплачивает воркер"
                          }
                          onClick={() => generateOne(r.code)}
                        >
                          <Clapperboard size={13} />
                          {generating
                            ? "в очереди…"
                            : run.busy
                              ? "ставлю в очередь…"
                              : newCost(r.code) !== null
                                ? `сгенерировать ≈ ${usd(newCost(r.code)!, 2)}`
                                : "сгенерировать"}
                        </Btn>
                      )}
                      {/* Задача Оли №313: «должна быть возможность перегенерировать, если кадр
                          получился неудачным». Только у готового — у пустого это обычный пакет.
                          Старый кадр останется на дорожке, пока новый не придёт. */}
                      {ready ? (
                        <Btn
                          tone="ghost"
                          disabled={run.busy || generating}
                          title={
                            generating
                              ? "перегенерация уже в очереди"
                              : "ПЛАТНО: сгенерировать этот ракурс заново; прежний кадр останется, пока новый не будет готов"
                          }
                          onClick={() => regenerate(r.code)}
                        >
                          <RefreshCw size={13} />
                          {run.busy
                            ? "ставлю в очередь…"
                            : redoCost(r.code) !== null
                              ? `перегенерировать ≈ ${usd(redoCost(r.code)!, 2)}`
                              : "перегенерировать"}
                        </Btn>
                      ) : null}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Btn
                      tone="ghost"
                      disabled={setViews.isPending}
                      title="добавить ракурс в проект (бесплатно, генерация — отдельной кнопкой)"
                      onClick={() => changeSet(r.code, true)}
                    >
                      <Plus size={13} />
                      добавить
                    </Btn>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Промпт ДВИЖЕНИЯ (для клипа) живёт на шаге «2 · Видео», панель «Движение кадров» (задача Оли
          №335: рядом с заданием ракурса два промпта — на фото и на видео — путались). Здесь только
          промпт фото: задание ракурса в строке. */}
      <ErrorLine text={setViews.error ? errorText(setViews.error) : ""} className="mt-2" />

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
        {missing.length === 0 ? (
          <Hint>Все ракурсы готовы.</Hint>
        ) : toRun.length === 0 ? (
          <Hint>Все недостающие ракурсы уже в очереди.</Hint>
        ) : (
          <>
            <Btn
              tone="primary"
              disabled={run.busy || estimate.isPending}
              title="ПЛАТНО: пакет ракурсов в очередь, оплачивает воркер"
              onClick={generate}
            >
              <Clapperboard size={13} />
              {run.busy
                ? "ставлю в очередь…"
                : estimate.data
                  ? `Сгенерировать все недостающие (${toRun.length}) ≈ ${usd(estimate.data.total, 2)}`
                  : `Сгенерировать все недостающие (${toRun.length})`}
            </Btn>
            {estimate.isError ? <Hint>оценка недоступна — кнопка всё равно сработает</Hint> : null}
          </>
        )}
      </div>
      <ErrorLine text={run.error} className="mt-2" />
      {run.data ? (
        <Hint className="mt-1">
          В очереди: {run.data.items.length}
          {run.data.items.length === 1 && rows.some((r) => r.code === run.data!.items[0]!.angle && r.shot && hasStill(r.shot.still))
            ? " — заменит картинку этого кадра, когда будет готова."
            : " — появятся на дорожке ниже."}
        </Hint>
      ) : null}

      {cropShot?.still ? (
        <CropDialog
          open={cropFor !== null}
          onClose={() => setCropFor(null)}
          pid={pid}
          variantId={cropShot.still.variant_id}
          assetPath={cropShot.still.asset_path}
          projectId={cropShot.source ? pid : null}
          shotId={cropShot.shot_id}
        />
      ) : null}

      <ShotStillPicker
        open={pickerFor !== null}
        onClose={() => setPickerFor(null)}
        pid={pid}
        shotId={pickerFor ?? 0}
        currentVariantId={pickerRow?.shot?.still?.variant_id ?? null}
      />
    </Panel>
  );
}
